import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// In production deployment, use same origin for API calls
// This handles custom domains like unclonedjournal.com
export const API = window.location.hostname.includes('preview.emergentagent.com') || window.location.hostname === 'localhost'
  ? `${BACKEND_URL}/api`
  : `${window.location.origin}/api`;

// SECURITY: Session tokens are stored ONLY in httpOnly cookies (set by backend)
// No tokens are stored in localStorage to prevent XSS attacks
// Configure axios to always include credentials (cookies) with requests
axios.defaults.withCredentials = true;

// Set global timeout to prevent requests from hanging forever
axios.defaults.timeout = 30000; // 30 seconds

// ==================== CSRF TOKEN MANAGEMENT ====================
let csrfToken = null;
let csrfTokenPromise = null; // Prevent race conditions with multiple simultaneous fetches

// Fetch CSRF token from backend
const fetchCSRFToken = async (forceRefresh = false) => {
  // If we already have a token and not forcing refresh, return it
  if (csrfToken && !forceRefresh) {
    return csrfToken;
  }
  
  // If a fetch is already in progress, wait for it
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }
  
  // Start new fetch
  csrfTokenPromise = (async () => {
    try {
      // Get session token from localStorage backup for header
      const sessionToken = localStorage.getItem('session_token');
      
      const response = await axios.get(`${API}/auth/csrf-token`, { 
        withCredentials: true,
        headers: { 
          'X-Skip-CSRF': 'true',
          // Include session token header for custom domains where cookies may fail
          ...(sessionToken && { 'X-Session-Token': sessionToken })
        }
      });
      csrfToken = response.data.csrf_token;
      console.debug('[CSRF] Token fetched successfully');
      return csrfToken;
    } catch (error) {
      console.debug('[CSRF] Failed to fetch token:', error.message);
      csrfToken = null;
      return null;
    } finally {
      csrfTokenPromise = null;
    }
  })();
  
  return csrfTokenPromise;
};

// Clear CSRF token (call on logout or session change)
const clearCSRFToken = () => {
  csrfToken = null;
  csrfTokenPromise = null;
};

// ==================== AXIOS REQUEST INTERCEPTOR ====================
axios.interceptors.request.use(
  async (config) => {
    const protectedMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    
    // Always add session token as header backup (for custom domains where cookies may not work)
    const sessionToken = localStorage.getItem('session_token');
    if (sessionToken) {
      config.headers['X-Session-Token'] = sessionToken;
    }
    
    if (protectedMethods.includes(config.method?.toUpperCase())) {
      // Skip CSRF for certain endpoints that don't need it
      // NOTE: Backend explicitly exempts /api/admin/* from CSRF middleware
      const skipPaths = [
        '/auth/login', 
        '/auth/signup', 
        '/auth/session', 
        '/auth/google',
        '/auth/callback',
        '/admin/', // ALL admin routes are CSRF-exempt on backend
        '/stripe/webhook', 
        '/stripe/guest-checkout', // Guest checkout from landing page (no auth)
        '/auth/check-duplicate-name', 
        '/auth/founding-member-signup',
        '/auth/founding-member-google-signup',
        '/auth/founding-member-status', 
        '/auth/onboarding',
        '/auth/forgot-password',
        '/auth/reset-password',
        '/auth/guest-signup',
        '/auth/register-invited',
        '/client-portal/login',
        '/b2b/login',
        '/b2b/signup',
        '/b2b/user-login',
        '/b2b/forgot-password',
        '/b2b/reset-password',
        '/public/' // Public endpoints don't need CSRF
      ];
      const shouldSkip = skipPaths.some(path => config.url?.includes(path));
      
      if (!shouldSkip && !config.headers['X-Skip-CSRF']) {
        // Fetch CSRF token if we don't have one
        if (!csrfToken) {
          await fetchCSRFToken();
        }
        
        if (csrfToken) {
          config.headers['X-CSRF-Token'] = csrfToken;
        }
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ==================== SESSION TOKEN MANAGEMENT ====================
export const SESSION_TOKEN_KEY = 'uncloned_session_token';

export const setSessionToken = (token) => {
  // Store token in localStorage as backup for custom domains where cookies may fail
  if (token) {
    localStorage.setItem('session_token', token);
    console.debug('[Security] Session token stored as backup');
    // Clear old CSRF token since session changed - will fetch new one on next request
    clearCSRFToken();
  }
};

export const getSessionToken = () => {
  return localStorage.getItem('session_token');
};

export const clearSessionToken = () => {
  // SECURITY: Clear all auth-related cookies and storage
  localStorage.removeItem(SESSION_TOKEN_KEY);
  localStorage.removeItem('session_token');
  localStorage.removeItem('uncloned_user');
  
  // Clear CSRF token
  clearCSRFToken();
  
  // Clear auth cookies by setting them to expire in the past
  const cookiesToClear = ['session_token', 'auth_token'];
  cookiesToClear.forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname};`;
  });
};

// Export function to refresh CSRF token (call after login)
export const refreshCSRFToken = () => fetchCSRFToken(true);

// Flag to prevent multiple redirects when session is invalid
let isRedirectingToLogin = false;
let isRedirectingToOnboarding = false;

// ==================== AXIOS RESPONSE INTERCEPTOR ====================
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle 403 CSRF validation failed - refresh token and retry ONCE
    if (error.response?.status === 403 && error.response?.data?.detail?.includes('CSRF validation failed')) {
      console.debug('[CSRF] Token invalid, refreshing and retrying...');
      
      // Only retry once to prevent infinite loops
      if (!error.config._csrfRetry) {
        error.config._csrfRetry = true;
        clearCSRFToken(); // Clear the invalid token
        
        const newToken = await fetchCSRFToken(true); // Force refresh
        if (newToken) {
          error.config.headers['X-CSRF-Token'] = newToken;
          console.debug('[CSRF] Retrying request with new token');
          return axios.request(error.config);
        }
      }
      
      // If retry failed, show user-friendly message
      console.error('[CSRF] Retry failed, user needs to refresh page');
      return Promise.reject(error);
    }
    
    // Handle 403 "Onboarding required" - redirect to onboarding page
    if (error.response?.status === 403 && error.response?.data?.detail === "Onboarding required") {
      const isOnOnboardingPage = window.location.pathname.includes('/onboarding');
      
      if (!isOnOnboardingPage && !isRedirectingToOnboarding) {
        console.debug('[Session] Received 403 - onboarding required, redirecting');
        isRedirectingToOnboarding = true;
        
        setTimeout(() => {
          window.location.href = '/onboarding';
        }, 100);
      }
      return Promise.reject(error);
    }
    
    // Handle 401 Unauthorized - session expired or invalid
    if (error.response?.status === 401) {
      const isAuthPage = 
        window.location.pathname === '/login' || 
        window.location.pathname === '/signup' ||
        window.location.pathname === '/founding-member' ||
        window.location.pathname.startsWith('/admin') || // Admin dashboard has separate auth
        window.location.pathname.startsWith('/client-portal') ||
        window.location.pathname.startsWith('/b2b') ||
        window.location.pathname.startsWith('/redeem'); // Public redeem page
      
      // Don't redirect if already on an auth page or if redirect is in progress
      if (!isAuthPage && !isRedirectingToLogin) {
        // Check if this is a "session expired" type error vs just an auth-required endpoint
        const errorDetail = error.response?.data?.detail || '';
        const isSessionError = 
          errorDetail.includes('Session expired') || 
          errorDetail.includes('Invalid session') ||
          errorDetail.includes('Not authenticated');
        
        if (isSessionError) {
          console.debug('[Session] Received 401 - session invalid, redirecting to login');
          isRedirectingToLogin = true;
          clearSessionToken();
          
          setTimeout(() => {
            window.location.href = '/login?session_expired=true';
          }, 100);
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Reset redirect flags when page loads (in case of manual navigation)
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    isRedirectingToLogin = false;
    isRedirectingToOnboarding = false;
  });
}

export default axios;
